import { useState, useEffect } from 'react';
import { dataTablesApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { MultiSelect } from '@/components/ui/multiselect';
import { Lock, Users, Globe, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { DataTable } from '@/types/data-tables';

interface Team {
  id: string;
  name: string;
}

interface ShareDataTableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  table: DataTable | null;
  teams: Team[];
  onSuccess?: () => void;
}

export function ShareDataTableDialog({
  open,
  onOpenChange,
  table,
  teams,
  onSuccess,
}: ShareDataTableDialogProps) {
  const [saving, setSaving] = useState(false);
  const [visibility, setVisibility] = useState<'private' | 'team' | 'public'>('private');
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);

  // Populate form when dialog opens with table data
  useEffect(() => {
    if (open && table) {
      setVisibility(table.visibility);
      setSelectedTeamIds(table.sharedTeams?.map((t) => t.teamId) || []);
    }
  }, [open, table]);

  const handleSave = async () => {
    if (!table) return;

    // Validate team selection when team visibility is chosen
    if (visibility === 'team' && selectedTeamIds.length === 0) {
      toast.error('Please select at least one team to share with');
      return;
    }

    try {
      setSaving(true);

      // Build shares array based on visibility
      const shares = visibility === 'team'
        ? selectedTeamIds.map(teamId => ({
            sharedWithType: 'team' as const,
            sharedWithId: teamId,
            permission: 'read' as const,
          }))
        : [];

      await dataTablesApi.updateShares(table.id, shares, visibility);

      toast.success('Sharing settings updated successfully');
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      console.error('Failed to update sharing settings:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to update sharing settings';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const teamOptions = teams.map((team) => ({
    value: team.id,
    label: team.name,
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share Data Table</DialogTitle>
          <DialogDescription>
            Choose who can access "{table?.name}"
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <RadioGroup
            value={visibility}
            onValueChange={(value) => setVisibility(value as 'private' | 'team' | 'public')}
            className="space-y-2"
          >
            {/* Private option */}
            <Label
              htmlFor="private"
              className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50 transition-colors [&:has([data-state=checked])]:border-primary"
            >
              <RadioGroupItem value="private" id="private" />
              <Lock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">Private</div>
                <div className="text-xs text-muted-foreground">Only you can access this table</div>
              </div>
            </Label>

            {/* Team option */}
            <div>
              <Label
                htmlFor="team"
                className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50 transition-colors [&:has([data-state=checked])]:border-primary"
              >
                <RadioGroupItem value="team" id="team" />
                <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">Team</div>
                  <div className="text-xs text-muted-foreground">Share with specific teams</div>
                </div>
              </Label>

              {/* Team selector - shown when team visibility is selected */}
              {visibility === 'team' && (
                <div className="mt-2 ml-10">
                  <MultiSelect
                    options={teamOptions}
                    value={selectedTeamIds}
                    onValueChange={setSelectedTeamIds}
                    placeholder="Select teams..."
                    emptyMessage="No teams available"
                  />
                </div>
              )}
            </div>

            {/* Public option */}
            <Label
              htmlFor="public"
              className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50 transition-colors [&:has([data-state=checked])]:border-primary"
            >
              <RadioGroupItem value="public" id="public" />
              <Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">Public</div>
                <div className="text-xs text-muted-foreground">Everyone in the organization can access</div>
              </div>
            </Label>
          </RadioGroup>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
